// columns-one.js

import * as echarts from 'echarts';
import moment from 'moment';
import comOpt from './common-options.json';
import { merge } from 'lodash';
import utils from "@/utils";
import i18n from '@/i18nSetup';
import { enableTouchTooltip, applyChartMotion, resolveWhenDrawn } from './echarts-touch';

const formatLabels = (data, period = 'daily') => {
  return data
    .map((day) => {
      let dateFormat = 'MMM DD';
      
      // Use different date formats based on period
      if (period === 'weekly') {
        dateFormat = 'MMM DD'; // Start of week
      } else if (period === 'monthly') {
        dateFormat = 'MMM YYYY';
      }
      
      return {
        ...day,
        unix: day.date,
        date: moment(day.date).format(dateFormat),
      };
    })
    .sort((a, b) => a.unix - b.unix);
};

const create = ({ page, ref, chartData, fields, category, color, period = 'daily' }) => {
  return new Promise((resolve, reject) => {
    const container = ref[0];
    const chart = echarts.init(container);

    const formattedData = formatLabels(chartData, period);

    const xAxisData = formattedData.map((item) => item[category]);

    // Prepare series data
    const series = fields.map((fieldItem) => {
      const data = formattedData.map((item) => {
        let value = item[fieldItem.field];
        // Convert unsubscribed values to negative
        if (fieldItem.field === 'unsubscribed') {
          // value = -value;
        }
        return value;
      });
      return {
        name: fieldItem.field === 'subscribed' ? i18n.t('DASHBOARD_SUBSCRIBED') : i18n.t('DASHBOARD_UNSUBSCRIBED'),
        type: 'bar',
        stack: 'total',
        data: data,
        //barWidth: '30%',
        barMaxWidth: 12,
        itemStyle: {
          borderRadius: 3,
          borderWidth: 0.5,
          borderColor: '#fff',
          color: (params) => {
            if (params.value < 0) {
              // Use a different color for negative values (unsubscribed)
              return echarts.color.lift(page.colorCode, -0.2);
            } else {
              return page.colorCode;
            }
          },
        },
        label: {
          show: false,
        },
        emphasis: {
          focus: 'series',
        },
      };
    });

    const option = merge({}, comOpt, {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params) => {
          if (params.length === 0) return '';
          
          let hasData = false;
          // Check if any of the values are non-zero
          params.forEach(item => {
            if (item.value != null && item.value !== 0) {
              hasData = true;
            }
          });
          
          // Only show tooltip if there's actual data
          if (!hasData) return '';
          
          let tooltipHtml = `<div class='tooltip'>
            <span class='date'>${params[0].axisValueLabel}
              <span class="soc-sea ${color}">
                <!-- Additional HTML can go here -->
              </span>
            </span>
            <ul class='tooltip-list'>`;

          params.forEach((item) => {
            if (item.value != null && item.value !== 0) {
              tooltipHtml += `<li data-value="${Math.abs(item.value)}">
                <div class="soc-sea ${color}">
                  ${item.seriesName}
                </div>${Math.abs(item.value)}</li>`;
            }
          });

          tooltipHtml += '</ul></div>';
          return tooltipHtml;
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
      },
      yAxis: {
        type: 'value',
      },
      series: series,
      color: [page.colorCode, echarts.color.lift(page.colorCode, -0.2)],
    });

    applyChartMotion(option, true);
    chart.setOption(option);
    enableTouchTooltip(chart);
    resolveWhenDrawn(chart, resolve);
  });
};

export async function exportChart({
  chart,
  container,
  chartData,
  page,
  fields,
  period,
  color,
  type = 'svg'
}) {
  if (!chart) {
    return;
  }

  const filename = `subscriptions_chart_${type === 'csv' ? 'data_' : ''}${new Date().toISOString().split('T')[0]}`;

  if (type === 'svg') {
    if (!container) {
      return;
    }

    try {
      if (chart && chart.dispose) {
        chart.dispose();
      }
      
      echarts.dispose(container);
      
      const svgChart = echarts.init(container, null, { renderer: 'svg' });
      
      const formattedData = formatLabels(chartData, period);
      const xAxisData = formattedData.map(item => item.date);
      
      const series = fields.map(fieldItem => {
        const data = formattedData.map(item => {
          let value = item[fieldItem.field];
          return value;
        });
        
        return {
          name: fieldItem.field === 'subscribed' ? i18n.t('DASHBOARD_SUBSCRIBED') : i18n.t('DASHBOARD_UNSUBSCRIBED'),
          type: 'bar',
          stack: 'total',
          data: data,
          barMaxWidth: 12,
          itemStyle: {
            borderRadius: 3,
            borderWidth: 0.5,
            borderColor: '#fff',
            color: fieldItem.field === 'subscribed' ? page.colorCode : echarts.color.lift(page.colorCode, -0.2)
          }
        };
      });
      
      const option = merge({}, comOpt, {
        backgroundColor: 'transparent',
        xAxis: {
          type: 'category',
          data: xAxisData,
        },
        yAxis: {
          type: 'value',
        },
        series: series,
        animation: false
      });
      
      svgChart.setOption(option);
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const svgElement = container.querySelector('svg');
      if (!svgElement) {
        throw new Error("SVG element not found in container");
      }
      
      const svgContent = svgElement.outerHTML;
      
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      
      utils.downloadFile(blob, `${filename}.svg`);
      
      if (svgChart) {
        svgChart.dispose();
      }

      const newChart = await create({
        page,
        ref: [container],
        chartData,
        fields,
        category: "date",
        color,
        period,
        renderer: 'canvas',
        animation: true
      });
      
      return newChart;
    } catch (error) {
      try {
        return await create({
          page,
          ref: [container],
          chartData,
          fields,
          category: "date",
          color,
          period,
          renderer: 'canvas',
          animation: true
        });
      } catch (revertError) {

      }
    }
  } else if (type === 'csv') {
    if (!chartData || chartData.length === 0) {
      return;
    }
    
    const categories = chartData.map(item => {
      const date = moment(item.date);
      if (period === 'weekly') {
        const weekNum = date.week();
        const year = date.year();
        return `Week ${weekNum}, ${year}`;
      } else if (period === 'monthly') {
        return date.format('MMM YYYY');
      }
      return date.format('MMM DD, YYYY');
    });

    const series = fields.map(fieldConfig => {
      return {
        name: fieldConfig.field === 'subscribed' ? i18n.t('DASHBOARD_SUBSCRIBED') : i18n.t('DASHBOARD_UNSUBSCRIBED'),
        data: chartData.map(item => item[fieldConfig.field] || 0)
      };
    });
    
    utils.exportDataToCSV({
      series,
      categories,
      seriesNames: series.map(s => s.name),
      filename
    });
  }
}

export default create;